import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Download, Images, ArrowLeft, UserPlus, Trash2, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { API, BACKEND_URL } from '../lib/constants';
import { PublicShell } from '../components/public/PublicShell';
import { LoadingScreen } from '../components/LoadingScreen';

const PrivacyStrip = ({ privacy, onDelete }) => {
  const active = privacy?.face_signature_active;
  const purgeDate = privacy?.face_purge_at ? new Date(privacy.face_purge_at).toLocaleDateString() : null;
  return (
    <div className="card p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="gallery-privacy-strip">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-4 h-4 text-lime shrink-0 mt-0.5" />
        <p className="text-xs text-slate-300 leading-relaxed">
          {active ? (
            <>Your face signature is active and auto-deletes on <span className="text-white font-semibold">{purgeDate}</span>. Your selfie was never stored.</>
          ) : (
            <>Your face signature has been <span className="text-white font-semibold">permanently purged</span>. Existing matches stay in this gallery.</>
          )}
        </p>
      </div>
      <button data-testid="delete-my-data-btn" onClick={onDelete} className="btn-danger shrink-0">
        <Trash2 className="w-3.5 h-3.5" /> Delete my data
      </button>
    </div>
  );
};

const GalleryPage = () => {
  const { galleryId, slug } = useParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/gallery/${galleryId}`)
      .then((r) => setGallery(r.data))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load gallery'))
      .finally(() => setLoading(false));
  }, [galleryId]);

  const downloadImage = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started');
  };

  const tenant = gallery?.tenant || null;
  const registerPath = tenant ? `/t/${tenant.slug}` : slug ? `/t/${slug}` : '/';

  const deleteData = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/gallery/${galleryId}`);
      toast.success('Your data was permanently deleted');
      navigate(registerPath);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete data');
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  if (loading) return <LoadingScreen label="Loading gallery…" />;

  if (error) {
    return (
      <PublicShell tenant={null}>
        <div className="card-brutal p-10 text-center max-w-md mx-auto" data-testid="error-message">
          <span className="w-12 h-12 bg-panel border-2 border-line text-slate-400 flex items-center justify-center mx-auto mb-5">
            <Images className="w-6 h-6" />
          </span>
          <h2 className="h-display text-2xl">Gallery not found</h2>
          <p className="text-sm text-slate-400 mt-2">{error}</p>
          <Link to={registerPath} className="btn-primary mt-6"><ArrowLeft className="w-4 h-4" /> Back</Link>
        </div>
      </PublicShell>
    );
  }

  const images = gallery?.images || [];

  return (
    <PublicShell
      tenant={tenant}
      right={
        <Link to={registerPath} className="btn-ghost !py-2" data-testid="back-to-home-btn">
          <UserPlus className="w-4 h-4" /> <span className="hidden sm:inline">Register another</span>
        </Link>
      }
    >
      <div className="mb-8 fade-up">
        <p className="eyebrow mb-3">Gallery <code className="ml-1 text-white">{galleryId}</code></p>
        <h1 className="h-display text-4xl sm:text-5xl leading-[0.95]" data-testid="gallery-title">{gallery.user_name}'s photos</h1>
        <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-slate-400" data-testid="gallery-count">
          {images.length} photo{images.length === 1 ? '' : 's'} matched to you
        </p>
      </div>

      <div className="mb-8 fade-up fade-up-delay-1">
        <PrivacyStrip privacy={gallery.privacy} onDelete={() => setConfirmOpen(true)} />
      </div>

      {images.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="gallery-grid">
          {images.map((image, index) => {
            const src = `${BACKEND_URL}${image.url}`;
            return (
              <div
                key={index}
                className="group relative aspect-square overflow-hidden bg-panel border-2 border-line hover:border-cyan transition-colors fade-up"
                style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
                data-testid={`gallery-image-${index}`}
              >
                <img src={src} alt={image.filename} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/60 transition-colors flex items-end p-3 opacity-0 group-hover:opacity-100">
                  <button
                    data-testid={`download-image-${index}-btn`}
                    onClick={() => downloadImage(src, image.filename)}
                    className="w-full inline-flex items-center justify-center gap-2 font-display font-bold uppercase tracking-wider text-xs px-3 py-2 bg-cyan text-ink border-2 border-cyan"
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card p-12 text-center" data-testid="no-photos-message">
          <span className="w-14 h-14 bg-panel border-2 border-line text-slate-500 flex items-center justify-center mx-auto mb-5">
            <Images className="w-7 h-7" />
          </span>
          <h2 className="h-display text-xl">No photos yet</h2>
          <p className="text-sm text-slate-400 mt-2 max-w-sm mx-auto">
            The photographers are still uploading. Every photo you appear in will show up here automatically — bookmark this page and check back.
          </p>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-surface border-2 border-red-500 rounded-none shadow-[0_18px_40px_rgba(224,69,123,0.18)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight text-white">Delete all my data?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This permanently erases your face signature, registration details and this gallery link. Photos you were matched in stay with the organiser but are no longer linked to you. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-data-cancel-btn" className="btn-ghost rounded-none border-line">Keep my data</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-data-confirm-btn" onClick={deleteData} disabled={deleting} className="rounded-none bg-red-500 hover:bg-red-600 text-white font-display font-bold uppercase tracking-wider">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete everything'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PublicShell>
  );
};

export default GalleryPage;
